import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import {
  DueDate,
  DueDateNoCarDependency,
  DueDateStats,
  DueDateVersion,
  RenewDueDates,
  RenewMyCarDueDate,
  SettleDueDates,
  UpsertDueDate,
} from "./types";

export const useMyCarDueDatesApi = () => {
  const api = useApi();

  const useMyCarDueDates = useCallback(
    <K extends keyof DueDateNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<DueDateNoCarDependency, K>>,
      signal: AbortSignal,
      carId: string,
      isTeam: boolean,
    ) => {
      const uri = isTeam ? "my_team_cars" : "my_cars";
      const url = buildUrl<NestedPick<DueDateNoCarDependency, K>>(`/${uri}/${carId}/due_dates`, query);
      return useSWR<Paginated<NestedPick<DueDateNoCarDependency, K>>, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<DueDateNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const renewDueDates = useCallback(
    async (body: RenewMyCarDueDate, dueDateId: string, carId: string): Promise<{ id: string }> => {
      const url = `/due_dates/${dueDateId}/renew`;

      const formData = new FormData();
      if (body.date) {
        formData.append("date", body.date);
      }

      if (body.files?.length) {
        body.files.forEach((file) => {
          formData.append("files", file, file.name);
        });
      }

      const id = await api.post<FormData, { id: string }>(url, formData);
      api.mutateCache([
        url,
        `/admin/due_dates`,
        `/admin/due_dates/status`,
        `/admin/due_dates/expiring_soon`,
        `/admin/due_dates/not_settled`,
        `/admin/due_dates/settled`,
        `/admin/cars/${carId}/due_dates`,
        `/my_cars/${carId}/due_dates`,
        `/my_team_cars/${carId}/due_dates`,
        `/admin/cars/notifications`,
      ]);
      return id;
    },
    [api],
  );

  return {
    useMyCarDueDates,
    renewDueDates,
  };
};

export const useDueDatesApi = () => {
  const api = useApi();

  const getAdminDueDate = useCallback(
    (dueDateId: string, signal: AbortSignal) => {
      const url = `/admin/due_dates/${dueDateId}`;
      return useSWRMutation<DueDate | null, CustomError, string>(url, (url) => api.get<DueDate>({ url, signal }));
    },
    [api],
  );

  const useAdminDueDates = useCallback(
    <K extends keyof DueDate>(
      query: PaginationQueryWithFields<NestedPick<DueDateNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
      isExpiringSoon?: boolean,
      isExpired?: boolean,
    ) => {
      const url = buildUrl<NestedPick<DueDateNoCarDependency, K>>(
        carId
          ? `/admin/cars/${carId}/due_dates`
          : isExpiringSoon
            ? "/admin/due_dates/expiring_soon"
            : isExpired
              ? "/admin/due_dates/expired"
              : "/admin/due_dates",
        query,
      );
      return useSWR<Paginated<NestedPick<DueDateNoCarDependency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<DueDateNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminDueDate = useCallback(
    async (body: UpsertDueDate): Promise<{ id: string }> => {
      const url = `/admin/due_dates`;
      const id = await api.post<UpsertDueDate, { id: string }>(url, body);
      api.mutateCache([
        url,
        `/admin/cars/${body.carId}/due_dates`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/overview`,
        `/admin/due_dates/status`,
        `/admin/cars/notifications`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminDueDate = useCallback(
    async (id: string, body: UpsertDueDate) => {
      const url = `/admin/due_dates/${id}`;
      await api.put<UpsertDueDate>(url, body);
      api.mutateCache([
        `/admin/due_dates`,
        `/admin/due_dates/expiring_soon`,
        `/admin/due_dates/not_settled`,
        `/admin/due_dates/settled`,
        `/admin/due_dates/`,
        `/admin/cars/${body.carId}/due_dates`,
        `/admin/due_dates/${id}/versions`,
        `/admin/due_dates/status`,
        `/admin/cars/notifications`,
      ]);
    },
    [api],
  );

  const deleteAdminDueDate = useCallback(
    async (dueDateId: string, carId?: string): Promise<void> => {
      const url = `/admin/due_dates/${dueDateId}`;
      await api.del(url).then(() => {
        api.mutateCache([
          `/admin/due_dates`,
          `/admin/cars/${carId}/due_dates`,
          `/admin/due_dates/status`,
          `/admin/cars/notifications`,
        ]);
      });
    },
    [api],
  );

  const getAdminDueDateHistory = useCallback(
    (query: PaginationQuery, dueDateID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/due_dates/${dueDateID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminDueDateVersion = useCallback(
    (dueDateId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/due_dates/${dueDateId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<DueDateVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<DueDateVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/due_dates/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const renewDueDates = useCallback(
    async (body: RenewDueDates, carIds: string[]): Promise<void> => {
      const url = `/admin/due_dates/renew`;
      await api.postNoResult<RenewDueDates>(url, body);
      const carIdsUrl = carIds.map((id) => `/admin/cars/${id}/due_dates`);
      api.mutateCache([
        url,
        `/admin/due_dates`,
        `/admin/due_dates/status`,
        `/admin/due_dates/expiring_soon`,
        `/admin/due_dates/not_settled`,
        `/admin/due_dates/settled`,
        `/admin/cars/notifications`,
        ...carIdsUrl,
      ]);
    },
    [api],
  );

  const bulkSettleDueDates = useCallback(
    async (body: SettleDueDates, carIds: string[]): Promise<void> => {
      const url = `/admin/due_dates/settle`;
      await api.postNoResult<SettleDueDates>(url, body);
      const carIdsUrl = carIds.map((id) => `/admin/cars/${id}/due_dates`);
      api.mutateCache([
        url,
        `/admin/due_dates`,
        `/admin/due_dates/status`,
        `/admin/due_dates/expiring_soon`,
        `/admin/due_dates/not_settled`,
        `/admin/due_dates/settled`,
        `/admin/cars/notifications`,
        ...carIdsUrl,
      ]);
    },
    [api],
  );

  const useDueDateStats = useCallback(
    (query: object, signal: AbortSignal) => {
      const url = buildUrl("/admin/due_dates/stats", query);
      return useSWR<DueDateStats | null, CustomError, string>(url, (url) => api.get<DueDateStats>({ url, signal }));
    },
    [api],
  );

  return {
    updateAdminDueDate,
    insertAdminDueDate,
    useAdminDueDates,
    getAdminDueDate,
    deleteAdminDueDate,
    getAdminDueDateHistory,
    getAdminDueDateVersion,
    getFieldFilters,
    renewDueDates,
    bulkSettleDueDates,
    useDueDateStats,
  };
};
